import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import { useCallback, useState } from "react";

import { Button, Card, CardContent, CardHeader } from "@material-ui/core";

import { useTenant } from "src/components/LoggedInAdminProvider";
import { tenantDeactivate } from "src/graphql/generated/tenantDeactivate";
import { tenantReactivate } from "src/graphql/generated/tenantReactivate";
import { DEACTIVATE_TENANT, REACTIVATE_TENANT } from "src/graphql/tenant";
import useMutation from "src/util/useMutation";

export default function Deactivate(): JSX.Element {
  const tenant = useTenant();
  const confirm = useConfirm();
  const [isSubmitting, setSubmitting] = useState(false);
  const snackbar = useSnackbar();
  const [deactivate] = useMutation<tenantDeactivate>(DEACTIVATE_TENANT, {
    onErrorSnackbarMessage: "Error deactivating tenant"
  });
  const [reactivate] = useMutation<tenantReactivate>(REACTIVATE_TENANT, {
    onErrorSnackbarMessage: "Error reactivating tenant"
  });

  const handleDeactivate = useCallback(async () => {
    try {
      await confirm({
        title: `Deactivate ${tenant!.name}`,
        description: `Are you sure you want to deactivate ${
          tenant!.name
        }? This will archive all active campaigns and release all messaging phone numbers.`
      });
    } catch {
      // user cancelled
      return;
    }

    setSubmitting(true);
    const { errors } = await deactivate();
    setSubmitting(false);
    if (!errors) {
      snackbar.enqueueSnackbar("Tenant deactivated");
    }
  }, [confirm, tenant, setSubmitting, deactivate, snackbar]);

  const handleReactivate = useCallback(async () => {
    try {
      await confirm({
        title: `Reactivate ${tenant!.name}`,
        description: `Are you sure you want to reactivate ${tenant!.name}?`
      });
    } catch {
      // user cancelled
      return;
    }

    setSubmitting(true);
    const { errors } = await reactivate();
    setSubmitting(false);
    if (!errors) {
      snackbar.enqueueSnackbar("Tenant reactivated");
    }
  }, [confirm, tenant, setSubmitting, reactivate, snackbar]);

  return (
    <Card>
      <CardHeader
        title={
          tenant?.deactivatedAt ? "Reactivate Tenant" : "Deactivate Tenant"
        }
      />
      <CardContent>
        {tenant?.deactivatedAt ? (
          <Button
            disabled={isSubmitting}
            onClick={handleReactivate}
            variant="contained"
            color="secondary"
          >
            Reactivate Tenant
          </Button>
        ) : (
          <Button
            disabled={isSubmitting}
            onClick={handleDeactivate}
            variant="contained"
            color="secondary"
          >
            Deactivate Tenant
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
